<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Sidebar from '@/manage/layouts/components/Sidebar.vue'
import Header from '@/manage/layouts/components/Header.vue'
import UpgradeCta from '@/manage/components/upgrade/UpgradeCta.vue'
import { useUserStore } from '@/manage/stores/user'
import {
  getManageViewKey,
  KEEP_ALIVE_MANAGE_VIEWS,
  useRecruitingAiV2InstanceKey,
} from '@/manage/composables/recruiting-ai-v2/instanceKey'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
watch(
  () => userStore.isAdminModeActive,
  () => {
    if (route.name === 'RecruitingAi' || route.name === 'RecruitingAiV2') {
      void router.replace({ name: 'Jobs' })
    } else if (route.name === 'TalentPool') {
      void router.replace({ name: 'TalentPool', query: { tag: '__all__' } })
    }
  },
  { flush: 'sync' },
)
const isCollapse = ref(false)

// Step 13 F-4: the V2 Agent page stays alive (KeepAlive, max 1) while the user is elsewhere, e.g. on
// Job Talent. Its key only changes for a different conversation, which gives a fresh instance and
// destroys the cached one; other pages keep their `route.path` key (unchanged).
const recruitingAiV2Key = useRecruitingAiV2InstanceKey(route)

// 记录进入窄屏前的侧边栏状态，用于恢复
let collapseBeforeNarrow = false
// 用户在窄屏下是否手动操作过
let userToggledInNarrow = false

const toggleCollapse = () => {
  isCollapse.value = !isCollapse.value
  // 如果当前在窄屏下手动操作，标记为用户主动选择
  if (window.innerWidth <= NARROW_BREAKPOINT) {
    userToggledInNarrow = true
  }
}

const collapseMainSidebar = () => {
  isCollapse.value = true
}

// 窄屏自动折叠：≤1280px 自动折叠，离开窄屏恢复之前状态
const NARROW_BREAKPOINT = 1280

function warmupManageRouteChunks() {
  const scheduler = window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
  }
  const warmup = () => {
    void Promise.allSettled([
      import('@/manage/views/workbench/WorkbenchView.vue'),
      import('@/manage/views/jobs/JobsView.vue'),
      import('@/manage/views/talent/TalentPoolView.vue'),
      import('@/manage/views/recruiting-ai/RecruitingAiView.vue'),
      import('@/manage/views/points/PointsRecordView.vue'),
    ])
  }

  if (typeof scheduler.requestIdleCallback === 'function') {
    scheduler.requestIdleCallback(warmup, { timeout: 2000 })
    return
  }

  globalThis.setTimeout(warmup, 800)
}

function handleResize() {
  const isNarrow = window.innerWidth <= NARROW_BREAKPOINT
  if (isNarrow && !isCollapse.value) {
    // 进入窄屏：保存当前状态并折叠
    collapseBeforeNarrow = false
    userToggledInNarrow = false
    isCollapse.value = true
  } else if (!isNarrow && isCollapse.value && !userToggledInNarrow) {
    // 离开窄屏：恢复之前状态（仅当用户未在窄屏下手动操作过）
    isCollapse.value = collapseBeforeNarrow
  }
}

// 监听路由变化，控制侧边栏折叠状态
watch(
  () => [route.path, route.query.jobId] as const,
  ([newPath]) => {
    // 招聘 AI 工作区默认保持主侧边栏收起，避免对话和推荐内容被挤压
    const isRecruitingAiPage = newPath === '/manage/recruiting-ai'

    if (isRecruitingAiPage) {
      isCollapse.value = true
    }
    // 其他情况保持当前状态不变
  },
  { immediate: true },
)

onMounted(() => {
  if (window.innerWidth <= NARROW_BREAKPOINT) {
    collapseBeforeNarrow = true // 初始就是窄屏，不自动恢复
    isCollapse.value = true
  }
  window.addEventListener('resize', handleResize)
  window.addEventListener('easyhire:collapse-main-sidebar', collapseMainSidebar)
  warmupManageRouteChunks()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('easyhire:collapse-main-sidebar', collapseMainSidebar)
})
</script>

<template>
  <div class="flex h-screen w-full overflow-hidden">
    <!-- 移动端遮罩层 -->
    <div
      v-if="!isCollapse"
      class="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
      @click="toggleCollapse"
    ></div>

    <aside
      class="shrink-0 overflow-hidden bg-white transition-[width] duration-300 max-md:absolute max-md:inset-y-0 max-md:left-0 max-md:z-50 md:relative"
      :class="[isCollapse ? 'w-16 max-md:w-0' : 'w-55 max-md:w-64 max-md:shadow-2xl']"
    >
      <Sidebar :isCollapse="isCollapse" />
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <header class="flex h-12 shrink-0 flex-col p-0">
        <div class="header-wrapper">
          <Header :isCollapse="isCollapse" @toggle-collapse="toggleCollapse" />
        </div>
      </header>

      <main class="manage-main">
        <div class="manage-page-stage">
          <router-view
            :key="`${userStore.user?.sub || ''}:${userStore.isAdminModeActive}`"
            v-slot="{ Component, route: viewRoute }"
          >
            <transition name="manage-page" appear>
              <keep-alive :include="KEEP_ALIVE_MANAGE_VIEWS" :max="1">
                <component
                  :is="Component"
                  v-if="Component"
                  :key="
                    viewRoute.name === 'RecruitingAi'
                      ? `${viewRoute.path}:${viewRoute.query.jobId || ''}`
                      : getManageViewKey(viewRoute, recruitingAiV2Key)
                  "
                />
              </keep-alive>
            </transition>
          </router-view>
        </div>
        <UpgradeCta />
      </main>
    </div>
  </div>
</template>

<style scoped>
.header-wrapper {
  height: 48px;
  width: 100%;
}

.manage-main {
  --manage-page-enter-duration: 160ms;
  --manage-page-leave-duration: 110ms;
  --manage-page-ease: cubic-bezier(0.22, 1, 0.36, 1);
  --manage-page-enter-offset: 4px;
  --manage-page-leave-offset: 2px;
  --manage-main-padding: 12px;
  flex: 1;
  min-height: 0;
  padding: var(--manage-main-padding);
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
}

.manage-page-stage {
  position: relative;
  height: 100%;
  min-height: 100%;
}

:deep(.manage-page-enter-active) {
  transition:
    opacity var(--manage-page-enter-duration) var(--manage-page-ease),
    transform var(--manage-page-enter-duration) var(--manage-page-ease);
  will-change: opacity, transform;
}

:deep(.manage-page-leave-active) {
  position: absolute;
  inset: 0;
  width: 100%;
  pointer-events: none;
  transition:
    opacity var(--manage-page-leave-duration) var(--manage-page-ease),
    transform var(--manage-page-leave-duration) var(--manage-page-ease);
  will-change: opacity, transform;
}

:deep(.manage-page-enter-from) {
  opacity: 0;
  transform: translate3d(0, var(--manage-page-enter-offset), 0);
}

:deep(.manage-page-leave-to) {
  opacity: 0;
  transform: translate3d(0, calc(-1 * var(--manage-page-leave-offset)), 0);
}

:deep(.manage-page-enter-to),
:deep(.manage-page-leave-from) {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

@media (prefers-reduced-motion: reduce) {
  :deep(.manage-page-enter-active),
  :deep(.manage-page-leave-active) {
    transition: none;
  }

  :deep(.manage-page-enter-from),
  :deep(.manage-page-leave-to) {
    opacity: 1;
    transform: none;
  }
}
</style>
